import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { exportItmOtmHistory } from '../../lib/excel-export'
import { ExcelDownloadButton } from '../ui/ExcelDownloadButton'
import { getStrikeStepForSymbol } from '../../config/constants'
import { GhostTh } from '../ui/ghost-table/StickyHeader'
import { TableWrapper } from '../ui/ghost-table/TableWrapper'
import { TableVirtuoso } from 'react-virtuoso'
import { formatISTTime, formatDateToDDMMYYYY, parseDateAndTimeToMs } from '../../lib/date-utils'
import { TABLE_CLASSES, formatIndianScale } from '../../lib/table-utils'
import { NumberCell } from '../ui/ghost-table/DataCells'
import { StrikeChip } from '../ui/ghost-table/StrikeChip'
import { DataChip } from '../ui/ghost-table/DataChip'
import { ColumnSelector } from '../ColumnSelector'
import { isSymbolLookupMatch } from '../../config/constants'
import { useSettingsStore } from '../../stores/settingsStore'

interface ItmOtmSectionProps {
    symbol: string
    expiry: string
    history: any[]
    rsiSeriesData?: any[]
    marketData?: any
}

const getOptionsForSymbolExpiry = (row: any, symbol: string, expiry: string): any[] => {
    if (row?.options && Array.isArray(row.options)) return row.options

    const optionsData = row?.optionsData
    if (!optionsData || !expiry) return []

    const direct = optionsData?.[symbol]?.[expiry]
    if (Array.isArray(direct)) return direct

    for (const [key, value] of Object.entries(optionsData)) {
        if (!isSymbolLookupMatch(key, symbol)) continue
        const byExpiry = (value as Record<string, unknown>)?.[expiry]
        if (Array.isArray(byExpiry)) return byExpiry
    }

    const symbolKeys = Object.keys(optionsData)
    if (symbolKeys.length === 1) {
        const onlyKey = symbolKeys[0]
        if (onlyKey && (isSymbolLookupMatch(onlyKey, symbol) || onlyKey.includes(symbol) || symbol.includes(onlyKey))) {
            const byExpiry = (optionsData[onlyKey] as Record<string, unknown>)?.[expiry]
            if (Array.isArray(byExpiry)) return byExpiry
        }
    }

    return []
}

type SwitcherType = 'Volume' | 'OI' | 'Chng in OI'
const TIME_GAPS = [1, 2, 3, 5, 10, 15, 20, 30, 60]

export const ItmOtmSection: React.FC<ItmOtmSectionProps> = ({ symbol, expiry, history = [], rsiSeriesData, marketData }) => {
    // State persistence keys
    const storageKey = `inspector_${symbol}_itm_otm`

    // Global User Selected Range (important for ITM/OTM aggregation)
    const settingsRange = useSettingsStore((s) => s.range)

    const [switcher, setSwitcher] = useState<SwitcherType>(() => {
        if (typeof window !== 'undefined')
            return (localStorage.getItem(`${storageKey}_switcher`) as SwitcherType) || 'OI'
        return 'OI'
    })

    const [timeGap, setTimeGap] = useState<number>(() => {
        if (typeof window !== 'undefined') return parseInt(localStorage.getItem(`${storageKey}_timeGap`) || '1', 10)
        return 1
    })

    const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
        if (typeof window !== 'undefined') return parseInt(localStorage.getItem(`${storageKey}_rows`) || '15', 10)
        return 15
    })

    const [includeAtm, setIncludeAtm] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`${storageKey}_includeAtm`)
            return saved === 'true'
        }
        return false
    })

    // Columns state
    const defaultColumns = [
        { id: 'datetime', label: 'Date Time', visible: true },
        { id: 'v1strike', label: 'V1 Strike', visible: true },
        { id: 'itm_call', label: 'ITM Call', visible: true },
        { id: 'itm_put', label: 'ITM Put', visible: true },
        { id: 'itm_net', label: 'ITM Net', visible: true },
        { id: 'otm_call', label: 'OTM Call', visible: true },
        { id: 'otm_put', label: 'OTM Put', visible: true },
        { id: 'otm_net', label: 'OTM Net', visible: true },
        { id: 'final_net', label: 'Final Net', visible: true },
        { id: 'index_rsi', label: 'Index RSI', visible: true },
        { id: 'index_ltp', label: 'Index LTP', visible: true },
    ]

    const [columns, setColumns] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`${storageKey}_columns`)
            if (saved) {
                try {
                    const parsed = JSON.parse(saved)
                    return defaultColumns.map((dc) => {
                        const savedCol = parsed.find((pc: any) => pc.id === dc.id)
                        return savedCol ? { ...dc, visible: savedCol.visible } : dc
                    })
                } catch (e) { console.warn('Ignored error in ItmOtmSection.tsx:', e); }
            }
        }
        return defaultColumns
    })

    // Persist state
    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem(`${storageKey}_switcher`, switcher)
            localStorage.setItem(`${storageKey}_timeGap`, timeGap.toString())
            localStorage.setItem(`${storageKey}_rows`, rowsPerPage.toString())
            localStorage.setItem(`${storageKey}_includeAtm`, includeAtm.toString())
            localStorage.setItem(
                `${storageKey}_columns`,
                JSON.stringify(columns.map((c) => ({ id: c.id, visible: c.visible }))),
            )
        }
    }, [switcher, timeGap, rowsPerPage, includeAtm, columns, storageKey])

    const handleColumnToggle = (id: string) => {
        setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c)))
    }

    const isVisible = (id: string) => columns.find((c) => c.id === id)?.visible

    // Process History
    const sortedHistory = useMemo(() => {
        let data = [...history].sort((a, b) => {
            const tA = parseDateAndTimeToMs(a.timestampIST || a.timestamp)
            const tB = parseDateAndTimeToMs(b.timestampIST || b.timestamp)
            return tB - tA
        })

        if (timeGap > 1) {
            data = data.filter((row) => {
                const timeStr = row.timestampIST || ''
                if (timeStr) {
                    // Quick minute extraction without local time conversion issues
                    const minMatch = timeStr.match(/:(\d{2}):/)
                    if (minMatch) {
                        return parseInt(minMatch[1], 10) % timeGap === 0
                    }
                }
                const timeMs = parseDateAndTimeToMs(row.timestampIST || row.timestamp)
                const time = new Date(timeMs)
                return time.getMinutes() % timeGap === 0
            })
        }
        return data
    }, [history, timeGap])

    const indexRsiSeries = useMemo(() => {
        return rsiSeriesData?.find((s: any) => s.name === symbol)?.data || []
    }, [rsiSeriesData, symbol])

    // Helper to enrich a single history row into ITM/OTM data
    const enrichRow = useCallback((row: any, step: number) => {
        const options = getOptionsForSymbolExpiry(row, symbol, expiry)

        let indexLtp =
            row.spotPrice ||
            row.indexLtp ||
            row.IndexLtp ||
            row.underlyingValue ||
            row.optionChainExtendedData?.assetLtpc?.ltp ||
            0
        if (!indexLtp && row.indexData && row.indexData[symbol]) {
            indexLtp = row.indexData[symbol].lastPrice || row.indexData[symbol].close || 0
        }

        let atmStrike = row.atmStrike || 0
        if (atmStrike <= 0 && indexLtp > 0) {
            atmStrike = Math.round(indexLtp / step) * step
        }
        if (atmStrike <= 0 && options.length > 0) {
            atmStrike = options[Math.floor(options.length / 2)]?.strike || options[0]?.strike || 0
        }

        const activeRange = settingsRange || 10
        const minStrike = atmStrike - activeRange * step
        const maxStrike = atmStrike + activeRange * step

        let itmCall = 0
        let itmPut = 0
        let otmCall = 0
        let otmPut = 0

        options.forEach((opt: any) => {
            if (opt.strike < minStrike || opt.strike > maxStrike) return
            if (!includeAtm && opt.strike === atmStrike) return // Remove ATM from ITM and OTM calculations if includeAtm is false

            let callVal = 0
            let putVal = 0

            if (switcher === 'Volume') {
                callVal = opt.callVolume || opt.ce?.volume || opt.ce?.vol || 0
                putVal = opt.putVolume || opt.pe?.volume || opt.pe?.vol || 0
            } else if (switcher === 'OI') {
                callVal = opt.callOpenInterest || opt.ce?.oi || opt.ce?.openInterest || 0
                putVal = opt.putOpenInterest || opt.pe?.oi || opt.pe?.openInterest || 0
            } else if (switcher === 'Chng in OI') {
                callVal = opt.callOpenInterestChange || opt.callChange || opt.ce?.oiChange || 0
                putVal = opt.putOpenInterestChange || opt.putChange || opt.pe?.oiChange || 0
            }

            if (opt.strike < atmStrike) {
                itmCall += callVal
            } else {
                otmCall += callVal
            }

            if (opt.strike > atmStrike) {
                itmPut += putVal
            } else {
                otmPut += putVal
            }
        })

        const itmNet = itmCall - itmPut
        const otmNet = otmPut - otmCall
        const finalNet = itmNet + otmNet

        let rowTime = typeof row.timestamp === 'number' ? row.timestamp : parseInt(row.timestamp, 10)
        if (!rowTime || isNaN(rowTime)) {
            rowTime = new Date(row.timestampIST || row.timestamp).getTime()
        }

        let closestDist = Infinity
        let bestRsiPoint: any = null

        for (const p of indexRsiSeries) {
            const pTimeMs = p.time * 1000
            const dist = Math.abs(pTimeMs - rowTime)
            if (dist < closestDist && dist <= 300000) {
                closestDist = dist
                bestRsiPoint = p
            }
        }

        const rsiVal = bestRsiPoint ? parseFloat(bestRsiPoint.value.toFixed(2)) : 0

        return {
            row,
            timestamp: row.timestampIST || row.timestamp,
            indexLtp,
            atmStrike,
            itmCall,
            itmPut,
            itmNet,
            otmCall,
            otmPut,
            otmNet,
            finalNet,
            rsiVal,
            isLive: false,
        }
    }, [symbol, expiry, switcher, indexRsiSeries, settingsRange, includeAtm])

    // Build Row Data from sortedHistory (already most-recent-first)
    const enrichedRows = useMemo(() => {
        const step = getStrikeStepForSymbol(symbol)

        // Build live row from marketData if available
        let liveRow: any = null
        if (marketData?.options && marketData.options.length > 0) {
            const liveSnap = {
                timestampIST: new Date().toISOString(),
                timestamp: Date.now(),
                spotPrice: marketData.spotPrice || 0,
                options: marketData.options,
                atmStrike: marketData.atmStrike || 0,
                indexData: { [symbol]: { lastPrice: marketData.spotPrice || 0 } },
            }
            liveRow = { ...enrichRow(liveSnap, step), isLive: true, timestamp: 'LIVE' }
        }

        const historyRows = sortedHistory.map((row: any) => enrichRow(row, step))

        // Prepend live row at the top (most recent first)
        return liveRow ? [liveRow, ...historyRows] : historyRows
    }, [sortedHistory, symbol, enrichRow, marketData])

    return (
        <React.Fragment>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                        {['Volume', 'OI', 'Chng in OI'].map((s) => (
                            <button
                                key={s}
                                onClick={() => setSwitcher(s as SwitcherType)}
                                className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
                                    switcher === s
                                        ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>

                    {/* ATM Toggle */}
                    <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                        <button
                            onClick={() => setIncludeAtm(!includeAtm)}
                            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-1.5 ${
                                includeAtm
                                    ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30'
                                    : 'bg-rose-500 text-white shadow-sm shadow-rose-500/30'
                            }`}
                        >
                            <span>ATM</span>
                            <span className="text-[10px] uppercase font-black">
                                {includeAtm ? 'ON' : 'OFF'}
                            </span>
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <ExcelDownloadButton
                        onExport={() => {
                            // For export, build enriched rows from ALL sortedHistory (not just paginated)
                            const step = getStrikeStepForSymbol(symbol)
                            const allRows = sortedHistory.map((row: any) => {
                                const options = getOptionsForSymbolExpiry(row, symbol, expiry)
                                let indexLtp = row.spotPrice || row.indexLtp || row.IndexLtp || row.underlyingValue || 0
                                if (!indexLtp && row.indexData && row.indexData[symbol]) {
                                    indexLtp = row.indexData[symbol].lastPrice || row.indexData[symbol].close || 0
                                }
                                let atmStrike = row.atmStrike || 0
                                if (atmStrike <= 0 && indexLtp > 0) atmStrike = Math.round(indexLtp / step) * step
                                const activeRange = settingsRange || 10
                                const minStrike = atmStrike - activeRange * step
                                const maxStrike = atmStrike + activeRange * step
                                let itmCall = 0, itmPut = 0, otmCall = 0, otmPut = 0
                                options.forEach((opt: any) => {
                                    if (opt.strike < minStrike || opt.strike > maxStrike) return
                                    let callVal = 0, putVal = 0
                                    if (switcher === 'Volume') { callVal = opt.callVolume || 0; putVal = opt.putVolume || 0 }
                                    else if (switcher === 'OI') { callVal = opt.callOpenInterest || 0; putVal = opt.putOpenInterest || 0 }
                                    else { callVal = opt.callOpenInterestChange || 0; putVal = opt.putOpenInterestChange || 0 }
                                    if (opt.strike < atmStrike) itmCall += callVal; else otmCall += callVal
                                    if (opt.strike > atmStrike) itmPut += putVal; else otmPut += putVal
                                })
                                return {
                                    timestamp: row.timestampIST || row.timestamp,
                                    atmStrike, indexLtp,
                                    itmCall, itmPut, itmNet: itmCall - itmPut,
                                    otmCall, otmPut, otmNet: otmPut - otmCall,
                                    rsiVal: 0,
                                }
                            })
                            exportItmOtmHistory(allRows, symbol, switcher)
                        }}
                        title="Download ITM/OTM Analysis as Excel"
                    />
                    <ColumnSelector columns={columns} onChange={handleColumnToggle} />

                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Interval:</span>
                        <select
                            value={timeGap}
                            onChange={(e) => setTimeGap(Number(e.target.value))}
                            className="text-xs font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors"
                        >
                            {TIME_GAPS.map((g) => (
                                <option key={g} value={g}>
                                    {g === 60 ? '1h' : `${g}m`}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Rows:</span>
                        <input
                            type="number"
                            min={1}
                            max={100}
                            value={rowsPerPage}
                            onChange={(e) => {
                                const val = Math.min(100, Math.max(1, Number(e.target.value) || 1));
                                setRowsPerPage(val);
                            }}
                            className="w-14 text-xs font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors text-center"
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <TableWrapper className="w-full shadow-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg transition-colors duration-300 content-auto">
                <div className="h-[400px]">
                    <TableVirtuoso
                        data={enrichedRows}
                        style={{ height: '100%' }}
                        fixedHeaderContent={() => (
                            <tr>
                                {isVisible('datetime') && (
                                    <th className="sticky left-0 z-50 px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider backdrop-blur-md">
                                        Date Time
                                    </th>
                                )}
                                {isVisible('v1strike') && (
                                    <GhostTh align="center" className="top-0! py-2 min-w-[100px]">
                                        <div className="leading-tight text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                            V1 Strike
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('itm_call') && (
                                    <GhostTh align="right" color="red" className="top-0! py-2">
                                        <div className="flex flex-col items-end gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                CE
                                            </span>
                                            <span className="leading-tight">ITM Call</span>
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('itm_put') && (
                                    <GhostTh align="right" color="green" className="top-0! py-2">
                                        <div className="flex flex-col items-end gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                PE
                                            </span>
                                            <span className="leading-tight">ITM Put</span>
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('itm_net') && (
                                    <GhostTh align="center" color="purple" className="top-0! py-2">
                                        <div className="flex flex-col items-center gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                Net
                                            </span>
                                            <span className="leading-tight">ITM Net</span>
                                            <span className="text-[8px] text-slate-400">(Call - Put)</span>
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('otm_call') && (
                                    <GhostTh align="right" color="red" className="top-0! py-2">
                                        <div className="flex flex-col items-end gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                CE
                                            </span>
                                            <span className="leading-tight">OTM Call</span>
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('otm_put') && (
                                    <GhostTh align="right" color="green" className="top-0! py-2">
                                        <div className="flex flex-col items-end gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                PE
                                            </span>
                                            <span className="leading-tight">OTM Put</span>
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('otm_net') && (
                                    <GhostTh align="center" color="purple" className="top-0! py-2">
                                        <div className="flex flex-col items-center gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter">
                                                Net
                                            </span>
                                            <span className="leading-tight">OTM Net</span>
                                            <span className="text-[8px] text-slate-400">(Put - Call)</span>
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('final_net') && (
                                    <GhostTh align="center" color="blue" className="top-0! py-2 bg-blue-50/30 dark:bg-blue-900/10">
                                        <div className="flex flex-col items-center gap-0.5">
                                            <span className="text-[9px] opacity-60 uppercase font-black tracking-tighter text-blue-600 dark:text-blue-400">
                                                Final
                                            </span>
                                            <span className="leading-tight font-black text-blue-700 dark:text-blue-300">Final Net</span>
                                            <span className="text-[8px] text-slate-400">ITM + OTM Net</span>
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('index_rsi') && (
                                    <GhostTh align="center" className="top-0! py-2">
                                        <div className="leading-tight text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                            Index RSI
                                        </div>
                                    </GhostTh>
                                )}
                                {isVisible('index_ltp') && (
                                    <GhostTh align="center" className="top-0! py-2">
                                        <div className="leading-tight text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                            Index LTP
                                        </div>
                                    </GhostTh>
                                )}
                            </tr>
                        )}
                        components={{
                            Table: (props) => <table {...props} className={TABLE_CLASSES.TABLE} />,
                            TableHead: React.forwardRef((props, ref) => <thead {...props} ref={ref as any} className={TABLE_CLASSES.THEAD} />),
                            TableBody: React.forwardRef((props, ref) => <tbody {...props} ref={ref as any} className="divide-y divide-slate-100 dark:divide-slate-800 bg-white/40 dark:bg-slate-900/40" />),
                            TableRow: (props) => <tr {...props} className={TABLE_CLASSES.TR} />
                        }}
                        itemContent={(index, r) => (
                            <>
                                {isVisible('datetime') && (
                                    <td className="sticky left-0 z-10 px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400 border-r border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm group-hover:bg-slate-50 dark:group-hover:bg-slate-800 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] transition-colors">
                                        {(() => {
                                            if (r.isLive) {
                                                return (
                                                    <div className="flex flex-col justify-center">
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 animate-pulse w-max">
                                                            ● LIVE
                                                        </span>
                                                        <span className="text-[9px] text-slate-400 font-medium font-sans mt-0.5">
                                                            Real-Time
                                                        </span>
                                                    </div>
                                                )
                                            }
                                            let time = '-'
                                            let date = ''
                                            const val = String(r.timestamp || r.row?.created_at || '')
                                            try {
                                                time = formatISTTime(val) || val
                                                if (typeof r.timestamp === 'number') {
                                                    date = formatDateToDDMMYYYY(new Date(r.timestamp))
                                                } else {
                                                    if (val.includes('T')) date = val.split('T')[0] ?? ''
                                                    else if (val.includes(' ')) date = val.split(' ')[0] ?? ''
                                                    else if (val.length > 7 && !val.includes(':')) date = val
                                                }
                                                if (time.includes('T')) time = time.split('T')[1]?.substring(0,5) ?? ''
                                                if (time.includes(' ')) time = time.split(' ')[1]?.substring(0,5) ?? ''
                                            } catch (e) { console.warn('Ignored error in ItmOtmSection.tsx:', e); }

                                            return (
                                                <div className="flex flex-col justify-center">
                                                    <span className="font-bold text-slate-700 dark:text-slate-200 text-xs text-left">
                                                        {time}
                                                    </span>
                                                    {date && date !== time && (
                                                        <span className="text-[9px] text-slate-400 font-medium font-sans">
                                                            {date}
                                                        </span>
                                                    )}
                                                </div>
                                            )
                                        })()}
                                    </td>
                                )}
                                {isVisible('v1strike') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <StrikeChip strike={r.atmStrike} type="neutral" />
                                    </td>
                                )}
                                {isVisible('itm_call') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <DataChip
                                            value={formatIndianScale(r.itmCall)}
                                            color="rose"
                                        />
                                    </td>
                                )}
                                {isVisible('itm_put') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <DataChip
                                            value={formatIndianScale(r.itmPut)}
                                            color="emerald"
                                        />
                                    </td>
                                )}
                                {isVisible('itm_net') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <DataChip
                                            value={(r.itmNet > 0 ? '+' : r.itmNet < 0 ? '-' : '') + (r.itmNet === 0 ? '0' : formatIndianScale(Math.abs(r.itmNet)))}
                                            color={r.itmNet > 0 ? 'emerald' : r.itmNet < 0 ? 'rose' : 'slate'}
                                            showArrow={true}
                                        />
                                    </td>
                                )}
                                {isVisible('otm_call') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <DataChip
                                            value={formatIndianScale(r.otmCall)}
                                            color="rose"
                                        />
                                    </td>
                                )}
                                {isVisible('otm_put') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <DataChip
                                            value={formatIndianScale(r.otmPut)}
                                            color="emerald"
                                        />
                                    </td>
                                )}
                                {isVisible('otm_net') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center`}>
                                        <DataChip
                                            value={(r.otmNet > 0 ? '+' : r.otmNet < 0 ? '-' : '') + (r.otmNet === 0 ? '0' : formatIndianScale(Math.abs(r.otmNet)))}
                                            color={r.otmNet > 0 ? 'emerald' : r.otmNet < 0 ? 'rose' : 'slate'}
                                            showArrow={true}
                                        />
                                    </td>
                                )}
                                {isVisible('final_net') && (
                                    <td className={`${TABLE_CLASSES.TD} text-center bg-blue-50/20 dark:bg-blue-900/10`}>
                                        <DataChip
                                            value={(r.finalNet > 0 ? '+' : r.finalNet < 0 ? '-' : '') + (r.finalNet === 0 ? '0' : formatIndianScale(Math.abs(r.finalNet)))}
                                            color={r.finalNet > 0 ? 'emerald' : r.finalNet < 0 ? 'rose' : 'slate'}
                                            showArrow={true}
                                        />
                                    </td>
                                )}
                                {isVisible('index_rsi') && (
                                    <td
                                        className={`${TABLE_CLASSES.TD} text-center font-bold text-xs text-slate-600 dark:text-slate-300`}
                                    >
                                        {r.rsiVal > 0 ? r.rsiVal.toFixed(2) : '-'}
                                    </td>
                                )}
                                {isVisible('index_ltp') && (
                                    <td
                                        className={`${TABLE_CLASSES.TD} text-center font-bold text-xs text-slate-700 dark:text-slate-200 bg-indigo-50/20 dark:bg-indigo-900/20`}
                                    >
                                        <NumberCell
                                            value={r.indexLtp}
                                            className="text-indigo-700 dark:text-indigo-400"
                                        />
                                    </td>
                                )}
                            </>
                        )}
                    />
                </div>
            </TableWrapper>
        </React.Fragment>
    )
}
